/**
 * 7-11 EMapSDK XML response parser
 * 資料源：POST https://emap.pcsc.com.tw/EMapSDK.aspx
 *   commandid=GetTown&cityid=XX       → 鄉鎮清單
 *   commandid=SearchStore&city=XX&town=YY → 門市清單
 */

export const CITY_CODES = Object.freeze([
  ['01', '台北市'], ['02', '基隆市'], ['03', '新北市'], ['04', '桃園市'],
  ['05', '新竹市'], ['06', '新竹縣'], ['07', '苗栗縣'], ['08', '台中市'],
  ['10', '彰化縣'], ['11', '南投縣'], ['12', '雲林縣'], ['13', '嘉義市'],
  ['14', '嘉義縣'], ['15', '台南市'], ['17', '高雄市'], ['19', '屏東縣'],
  ['20', '宜蘭縣'], ['21', '花蓮縣'], ['22', '台東縣'], ['23', '澎湖縣'],
  ['24', '連江縣'], ['25', '金門縣'],
])

const TAG = (name) => new RegExp(`<${name}>([\\s\\S]*?)</${name}>`)

const extractTag = (block, name) => {
  const m = block.match(TAG(name))
  return m ? m[1].trim() : ''
}

export const parseCoord = (raw) => {
  if (raw == null || String(raw).trim() === '') return null
  const n = Number(raw)
  if (!Number.isFinite(n)) return null
  return Math.abs(n) > 1000 ? n / 1e6 : n
}

// 7-11 service prefix 固定 2 碼：數字+英數（03ATM）或大寫+數字（A2、B0）
export const parseServices = (raw) => {
  if (!raw) return []
  return raw
    .split(',')
    .map((s) => s.replace(/^(?:\d[0-9A-Z]|[A-Z]\d)/, '').trim())
    .filter(Boolean)
}

const iterateGeoPositions = function* (xml) {
  const re = /<GeoPosition>([\s\S]*?)<\/GeoPosition>/g
  let m
  while ((m = re.exec(xml))) yield m[1]
}

export const parseStoresXml = (xml) => {
  if (typeof xml !== 'string' || !xml.includes('<GeoPosition>')) return []
  const stores = []
  for (const block of iterateGeoPositions(xml)) {
    const storeId = extractTag(block, 'POIID')
    const storeName = extractTag(block, 'POIName')
    if (!storeId || !storeName) continue
    stores.push({
      storeId,
      storeName,
      lng: parseCoord(extractTag(block, 'X')),
      lat: parseCoord(extractTag(block, 'Y')),
      phone: extractTag(block, 'Telno') || null,
      address: extractTag(block, 'Address'),
      services: parseServices(extractTag(block, 'StoreImageTitle')),
      opDay: extractTag(block, 'OP_DAY') || null,
      opTime: extractTag(block, 'OP_TIME') || null,
    })
  }
  return stores
}

export const parseTownsXml = (xml) => {
  if (typeof xml !== 'string' || !xml.includes('<GeoPosition>')) return []
  const towns = []
  for (const block of iterateGeoPositions(xml)) {
    const townId = extractTag(block, 'TownID')
    const townName = extractTag(block, 'TownName')
    if (!townName) continue
    towns.push({ townId, townName })
  }
  return towns
}

/**
 * 把 scraper 爬到的 raw store 轉成 Supabase cvs_stores row
 */
export const toStoreRow = (raw, { city, town, chain = '7-11' }) => ({
  store_id: raw.storeId,
  store_name: raw.storeName,
  chain,
  city,
  town,
  address: raw.address,
  phone: raw.phone,
  lng: raw.lng,
  lat: raw.lat,
  op_time: raw.opTime,
  op_day: raw.opDay,
  services: raw.services,
  is_active: true,
  last_synced_at: new Date().toISOString(),
})

/**
 * 從門市地址抽出「路段」名稱（第 3 層分類）
 *   '台北市松山區敦化北路168號B2' → '敦化北路'
 *   '台北市松山區民生東路五段52號' → '民生東路五段'
 *   '花蓮縣花蓮市中正村1鄰15號' → '中正村'（鄉下無正式路名的 fallback）
 */
export const parseStreet = (address, city, town) => {
  if (!address || typeof address !== 'string') return ''
  let s = address
  if (city) s = s.replace(city, '')
  if (town) s = s.replace(town, '')
  s = s.trim()
  // 主要格式：路/街/大道 + 可選「X段」
  const m = s.match(/^(.+?(?:路|街|大道))([一二三四五六七八九十]段)?/)
  if (m) return m[0]
  // fallback：切第一個數字前的內容
  const idx = s.search(/\d/)
  return (idx > 0 ? s.slice(0, idx) : s).trim()
}

/**
 * 從 stores 聚合出路段清單 [{ name, count }]，count 降冪排序
 */
export const groupByStreet = (stores) => {
  if (!Array.isArray(stores)) return []
  const map = new Map()
  for (const s of stores) {
    const street = parseStreet(s.address, s.city, s.town)
    if (!street) continue
    map.set(street, (map.get(street) || 0) + 1)
  }
  return [...map.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
}
